import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Grid from '@mui/material/Grid';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { BillingRecordWithDetails } from '../../types';

const BillingDetailPage: React.FC = () => {
  const { companyId, recordId } = useParams<{ companyId: string; recordId: string }>();
  const navigate = useNavigate();
  const { managementToken } = useManagementAuth();
  const [record, setRecord] = useState<BillingRecordWithDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!managementToken || !companyId || !recordId) return;
      try {
        const data = await api.managementCompany.getBillingDetail(companyId, recordId, managementToken);
        setRecord(data);
      } catch (error) {
        console.error('利用明細詳細の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [managementToken, companyId, recordId]);

  const formatAmount = (amount: number) => `${amount.toLocaleString()}円`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!record) {
    return <Typography color="error">利用明細が見つかりません</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/management/company/${companyId}/billing`)}
        sx={{ mb: 2 }}
      >
        利用明細一覧に戻る
      </Button>

      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
        {record.billing_year}年{record.billing_month}月 利用明細
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {record.period_start} ~ {record.period_end}
      </Typography>

      {/* サマリ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">対象店舗数</Typography>
              <Typography variant="h6" fontWeight="bold">{record.target_store_count}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">対象人数</Typography>
              <Typography variant="h6" fontWeight="bold">{record.target_worker_count}人</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">対象求人数</Typography>
              <Typography variant="h6" fontWeight="bold">{record.target_job_count}件</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">立替払い金額</Typography>
              <Typography variant="h6" fontWeight="bold">{formatAmount(record.prepaid_amount_total ?? 0)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                （税別）
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">振込関連手数料</Typography>
              <Typography variant="h6" fontWeight="bold">{formatAmount(record.transfer_fee)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                振込手数料: {formatAmount(record.bank_transfer_fee ?? 0)}<br />
                即給振込手数料: {formatAmount(record.sokyu_transfer_fee ?? 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">請求額調整</Typography>
              <Typography variant="h6" fontWeight="bold">{formatAmount(record.billing_adjustment)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">システム利用料</Typography>
              <Typography variant="h6" fontWeight="bold">{formatAmount(record.system_usage_fee)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">合計請求額</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">{formatAmount(record.total_amount)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 店舗別明細 */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>店舗名</TableCell>
                  <TableCell align="center">対象人数</TableCell>
                  <TableCell align="center">求人件数</TableCell>
                  <TableCell align="center">小計</TableCell>
                  <TableCell align="center">振込手数料</TableCell>
                  <TableCell align="center">即給振込手数料</TableCell>
                  <TableCell align="center">請求額調整</TableCell>
                  <TableCell align="center">システム利用料</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.details.map((detail) => (
                  <TableRow key={detail.id}>
                    <TableCell>{detail.store_name || '-'}</TableCell>
                    <TableCell align="center">{detail.worker_count}人</TableCell>
                    <TableCell align="center">{detail.job_count}件</TableCell>
                    <TableCell align="center">{formatAmount(detail.subtotal)}</TableCell>
                    <TableCell align="center">{formatAmount(detail.transfer_fee)}</TableCell>
                    <TableCell align="center">{formatAmount(detail.sokyu_transfer_fee ?? 0)}</TableCell>
                    <TableCell align="center">{formatAmount(detail.billing_adjustment)}</TableCell>
                    <TableCell align="center">{formatAmount(detail.system_usage_fee)}</TableCell>
                  </TableRow>
                ))}
                {record.details.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">明細データがありません</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingDetailPage;
